import { motion, AnimatePresence } from "framer-motion";
import {
  Wrench,
  Check,
  X,
  CaretDown,
  CaretRight,
  Brain,
} from "@phosphor-icons/react";
import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import { cn } from "../../lib/cn";
import { LeverMark } from "../../lib/brand";
import { computeLineDiff, type DiffLine, type DiffHunk } from "../../components/diff/DiffEngine";
import type { ChatMessage as ChatMessageType, ToolCallEvent, ToolResultEvent, EditEvent } from "../../types/ai";

interface ChatMessageProps {
  message: ChatMessageType;
  isStreaming?: boolean;
  streamingContent?: string;
  streamingThinking?: string;
}

const TOOL_LABELS_FALLBACK: Record<string, string> = {
  read_document: "Reading document",
  edit_document: "Editing document",
  edit_lines: "Editing lines",
  search_document: "Searching document",
  read_section: "Reading section",
  get_outline: "Getting outline",
  compile_latex: "Compiling LaTeX",
  get_errors: "Checking errors",
  insert_template: "Inserting template",
  add_package: "Adding package",
  manage_bibliography: "Managing bibliography",
  explain: "Explaining",
};

function getToolLabel(tc: ToolCallEvent): string {
  const p = tc.params;
  switch (tc.tool) {
    case "read_document": {
      const s = p.start_line as number | undefined;
      const e = p.end_line as number | undefined;
      if (s && e) return `Reading lines ${s}–${e}`;
      return "Reading document";
    }
    case "edit_document": {
      if (p.search) {
        const preview = String(p.search).slice(0, 30);
        return `Replacing "${preview}${String(p.search).length > 30 ? "…" : ""}"`;
      }
      return "Editing document";
    }
    case "edit_lines": {
      const s = p.start_line as number | undefined;
      const e = p.end_line as number | undefined;
      if (s && e) return `Editing lines ${s}–${e}`;
      return "Editing lines";
    }
    case "search_document": {
      if (p.pattern) {
        const preview = String(p.pattern).slice(0, 25);
        return `Searching "${preview}${String(p.pattern).length > 25 ? "…" : ""}"`;
      }
      return "Searching document";
    }
    case "read_section": {
      if (p.heading) return `Reading §${p.heading}`;
      return "Reading section";
    }
    default:
      return TOOL_LABELS_FALLBACK[tc.tool] || tc.tool;
  }
}


function ToolCallBadge({
  tc,
  result,
  edits,
}: {
  tc: ToolCallEvent;
  result?: ToolResultEvent;
  edits?: EditEvent[];
}) {
  const [expanded, setExpanded] = useState(false);
  const label = getToolLabel(tc);
  const hasInlineEdits = edits && edits.length > 0;

  return (
    <div className="my-0.5">
      <button
        onClick={() => setExpanded(!expanded)}
        className={cn(
          "flex items-center gap-1.5 rounded-[var(--radius-sm)] px-2 py-0.5 text-[10px]",
          "bg-[var(--bg-surface-hover)] text-[var(--text-muted)]",
          "hover:text-[var(--text-secondary)] transition-colors duration-100",
          "border border-[var(--border-hairline)]",
        )}
      >
        <Wrench size={10} weight="bold" />
        <span>{label}</span>
        {result && (
          result.success
            ? <Check size={9} weight="bold" className="text-[var(--color-signal-green)]" />
            : <X size={9} weight="bold" className="text-[var(--color-soft-red)]" />
        )}
        {!hasInlineEdits && (
          <CaretDown
            size={8}
            className={cn("transition-transform duration-100", expanded && "rotate-180")}
          />
        )}
      </button>

      {/* Non-edit tools: expandable raw output */}
      {!hasInlineEdits && (
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.12 }}
              className="mt-1 overflow-hidden rounded-[var(--radius-sm)] bg-[var(--bg-surface-hover)] border border-[var(--border-hairline)] p-2 text-[10px] font-mono text-[var(--text-muted)]"
            >
              {result?.output ? (
                <pre className="whitespace-pre-wrap break-all max-h-[200px] overflow-y-auto">{result.output}</pre>
              ) : (
                <pre className="whitespace-pre-wrap break-all">{JSON.stringify(tc.params, null, 2)}</pre>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      )}

      {/* Edit tools: inline diff blocks */}
      {hasInlineEdits &&
        edits.map((edit, idx) => (
          <EditDiffBlock key={idx} edit={edit} />
        ))}
    </div>
  );
}

function DiffLineRow({ line }: { line: DiffLine }) {
  const isAdd = line.type === "add";
  const isRemove = line.type === "remove";

  return (
    <div
      className={cn(
        "flex font-mono text-[10px] leading-[1.65] min-w-0",
        isAdd && "bg-[var(--color-signal-green)]/[0.07]",
        isRemove && "bg-[var(--color-soft-red)]/[0.07]",
      )}
    >
      <span
        className={cn(
          "select-none shrink-0 w-[26px] text-right pr-1 border-r",
          "text-[9px] text-[var(--text-muted)] opacity-40",
          isAdd ? "border-[var(--color-signal-green)]/15" : isRemove ? "border-[var(--color-soft-red)]/15" : "border-[var(--border-hairline)]",
        )}
      >
        {!isAdd ? (line.oldLineNumber ?? "") : ""}
      </span>
      <span
        className={cn(
          "select-none shrink-0 w-[26px] text-right pr-1 border-r",
          "text-[9px] text-[var(--text-muted)] opacity-40",
          isAdd ? "border-[var(--color-signal-green)]/15" : isRemove ? "border-[var(--color-soft-red)]/15" : "border-[var(--border-hairline)]",
        )}
      >
        {!isRemove ? (line.newLineNumber ?? "") : ""}
      </span>
      <span
        className={cn(
          "select-none shrink-0 w-[14px] text-center font-bold",
          isAdd && "text-[var(--color-signal-green)]",
          isRemove && "text-[var(--color-soft-red)]",
        )}
      >
        {isAdd ? "+" : isRemove ? "−" : " "}
      </span>
      <span
        className={cn(
          "flex-1 whitespace-pre-wrap break-all pl-0.5 pr-2",
          isAdd && "text-[var(--color-signal-green)]",
          isRemove && "text-[var(--color-soft-red)] line-through decoration-[var(--color-soft-red)]/30",
          !isAdd && !isRemove && "text-[var(--text-muted)]",
        )}
      >
        {line.content || " "}
      </span>
    </div>
  );
}

function EditDiffBlock({ edit }: { edit: EditEvent }) {
  const [collapsed, setCollapsed] = useState(false);

  const oldText = edit.oldContent ?? edit.search ?? "";
  const newText = edit.newContent ?? edit.replace ?? edit.content ?? "";

  if (!oldText && !newText) return null;

  const diff = computeLineDiff(oldText, newText);
  if (diff.stats.additions === 0 && diff.stats.deletions === 0) return null;

  const displayHunks = diff.hunks.length > 0 ? diff.hunks : [{ id: 0, lines: diff.lines } as DiffHunk];

  return (
    <div className="my-1.5 rounded-[var(--radius-md)] border border-[var(--border-subtle)] overflow-hidden">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className={cn(
          "flex items-center gap-1.5 w-full px-2.5 py-1 text-[10px]",
          "bg-[var(--bg-base)]",
          "hover:bg-[var(--bg-surface-hover)] transition-colors duration-75",
        )}
      >
        <span className="text-[var(--text-muted)] font-medium">Changes</span>
        <span className="ml-auto flex items-center gap-2 font-mono font-semibold">
          {diff.stats.additions > 0 && (
            <span className="text-[var(--color-signal-green)]">+{diff.stats.additions}</span>
          )}
          {diff.stats.deletions > 0 && (
            <span className="text-[var(--color-soft-red)]">−{diff.stats.deletions}</span>
          )}
          <CaretDown
            size={8}
            className={cn(
              "text-[var(--text-muted)] transition-transform duration-100",
              !collapsed && "rotate-180",
            )}
          />
        </span>
      </button>

      <AnimatePresence initial={false}>
        {!collapsed && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden border-t border-[var(--border-hairline)]"
          >
            <div className="max-h-[300px] overflow-y-auto overflow-x-auto bg-[var(--bg-base)]">
              {displayHunks.map((hunk, hi) => (
                <div key={hi}>
                  {hi > 0 && (
                    <div className="flex items-center gap-2 px-2 py-0.5 text-[9px] text-[var(--text-muted)] opacity-40 select-none">
                      <div className="flex-1 h-px bg-[var(--border-hairline)]" />
                      <span className="font-mono">···</span>
                      <div className="flex-1 h-px bg-[var(--border-hairline)]" />
                    </div>
                  )}
                  {hunk.lines.map((line, li) => (
                    <DiffLineRow key={`${hi}-${li}`} line={line} />
                  ))}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ThinkingBlock({ content }: { content: string }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="my-1">
      <button
        onClick={() => setExpanded(!expanded)}
        className={cn(
          "flex items-center gap-1.5 rounded-[var(--radius-sm)] px-2 py-0.5 text-[10px]",
          "text-[var(--text-muted)] hover:text-[var(--text-secondary)]",
          "transition-colors duration-100",
        )}
      >
        <Brain size={11} weight="duotone" className="text-[var(--text-muted)]" />
        <span className="font-medium">Thought for a moment</span>
        <CaretRight
          size={9}
          weight="bold"
          className={cn("transition-transform duration-100", expanded && "rotate-90")}
        />
      </button>
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="prose-chat-thinking mt-1 ml-2 pl-2.5 border-l-2 border-[var(--border-hairline)] text-[10px] text-[var(--text-muted)] leading-relaxed">
              <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkMath]}
                rehypePlugins={[[rehypeKatex, { strict: false }]]}
              >
                {preprocessMath(content)}
              </ReactMarkdown>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function preprocessMath(text: string): string {
  return text
    .replace(/\\\[(.+?)\\\]/gs, (_m, inner) => `$$${inner}$$`)
    .replace(/\\\((.+?)\\\)/gs, (_m, inner) => `$${inner}$`);
}

function MarkdownContent({ content }: { content: string }) {
  return (
    <div className="prose-chat text-[11px] text-[var(--text-secondary)] leading-relaxed">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[[rehypeKatex, { strict: false }]]}
      >
        {preprocessMath(content)}
      </ReactMarkdown>
    </div>
  );
}

export function ChatMessageComponent({ message, isStreaming, streamingContent, streamingThinking }: ChatMessageProps) {
  const isUser = message.role === "user";
  const content = isStreaming ? (streamingContent || "") : message.content;
  const thinking = isStreaming ? (streamingThinking || "") : (message.thinking || "");

  if (isUser) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.12 }}
        className="flex justify-end px-3 py-1.5"
      >
        <div className="max-w-[85%] rounded-[var(--radius-md)] bg-[var(--bg-surface-hover)] px-2.5 py-1.5 text-[11px] text-[var(--text-primary)] whitespace-pre-wrap break-words">
          {content}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.12 }}
      className="flex gap-2 px-3 py-1.5"
    >
      <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full mt-0.5 bg-[var(--bg-surface-hover)] text-[var(--text-muted)]">
        <LeverMark size={11} />
      </div>

      <div className="flex-1 min-w-0 space-y-0.5">
        {thinking && <ThinkingBlock content={thinking} />}
        {message.toolCalls?.map((tc, idx) => {
          const associatedEdits = message.edits?.filter(
            (e) => e.toolCallId === tc.id,
          );
          return (
            <ToolCallBadge
              key={tc.id || idx}
              tc={tc}
              result={message.toolResults?.find((r) => r.id === tc.id)}
              edits={associatedEdits && associatedEdits.length > 0 ? associatedEdits : undefined}
            />
          );
        })}
        {message.edits
          ?.filter((e) => !e.toolCallId)
          .map((edit, idx) => (
            <EditDiffBlock key={`orphan-edit-${idx}`} edit={edit} />
          ))}
        <div className="pr-2">
          <MarkdownContent content={content} />
          {isStreaming && (
            <span className="inline-block w-1 h-3 ml-0.5 bg-[var(--text-primary)] animate-pulse rounded-sm" />
          )}
        </div>
      </div>
    </motion.div>
  );
}
